// @ts-check
/**
 * @file Component: Storelocator v2 glue.
 *
 * Thin olmokit/Blade layer that drives the framework-agnostic MapEngine (see
 * ./engine). Responsibilities: DOM roots, doT result/popup templates,
 * Searchaddress + geolocation-on-load, legend filter state, deeplink, and the
 * (Phase-1 client-side) data fetch. The engine owns all map behavior.
 */
import { $, $all, on, forEach, setDataAttr } from "@olmokit/dom";
import ajax from "@olmokit/core/ajax";
import "@olmokit/core/progress/loading.scss";
import { render } from "@olmokit/utils/render";
import { debounce, getUrlQueryParams } from "@olmokit/utils";
import { navigateToMergedParams } from "@olmokit/browser";
import { globalConf } from "@olmokit/core/data";
import { dataLayerStore } from "components/Datalayer";
import { Searchaddress } from "components/Searchaddress";
import {
  GMAP_DEFAULT_RADIUS_MILES,
  GMAP_MAP_ID,
  GMAP_MIN_ZOOM,
  GMAP_RADIUS_CAP_MILES,
  GMAP_USA_BOUNDS,
  GMAP_USA_CENTER,
} from "config/index";
import { MapEngine } from "./engine/MapEngine";
import { toCategory } from "./engine/category";
import type {
  Category,
  LatLng,
  MapEngineInstance,
  RawStore,
  Store,
} from "./engine/types";

/** Phase-2 swap-point: when the CMS endpoint accepts lat/lng/radius, flip true. */
const SERVER_SIDE_FILTERING = false;

const CATEGORIES: Category[] = ["company", "premium", "dealer"];

export function bootStorelocatorV2() {
  const $root = $(".Storelocator:") as HTMLElement | undefined;
  if (!$root) return;

  const $map = $(".locator:map__holder", $root) as HTMLElement;
  const $results = $(".locator:results__list", $root) as HTMLElement;
  // The results COLUMN is the scroll container on desktop (overflow:auto);
  // `$results` is just the inner list.
  const $resultsPanel = $(".locator:results", $root) as HTMLElement | undefined;
  const renderResults = render($("#locator-results").innerHTML);
  const renderInfoWindow = render($("#locator-infowindow").innerHTML);

  const endpoint = globalConf.cmsApiUrl + "/getstorelocator";

  let engine: MapEngineInstance | null = null;
  let allStores: Store[] | null = null;
  /** Currently selected store (list click or marker click), for the highlight. */
  let selectedUuid: string | null = null;
  /** A just-issued scroll intent that must survive a list re-render (see selectStore). */
  let pendingScroll: { uuid: string; at: number } | null = null;
  const activeFilters: Record<Category, boolean> = {
    company: true,
    premium: true,
    dealer: true,
  };

  // ---- deeplink (read-only legacy→new translation) ----
  const deep = normalizeDeeplink(getUrlQueryParams() as Record<string, string>);
  const initialCenter: LatLng = deep.center || GMAP_USA_CENTER;
  let radiusMiles = deep.radius || GMAP_DEFAULT_RADIUS_MILES;

  // Radius behavior A/B (test period): follow-on-pan is the DEFAULT;
  // `?radiusmode=locked` pins the radius to the searched/fallback location.
  const panFollowsRadius =
    (getUrlQueryParams() as Record<string, string>).radiusmode !== "locked";

  // ---- data normalization ----
  function toStore(r: RawStore): Store {
    const s: Store = {
      uuid: r.uuid,
      name: r.name,
      lat: Number(r.lat),
      lng: Number(r.lng),
      addr_1: r.addr_1,
      addr_2: r.addr_2,
      markertype: r.markertype,
      phone: r.phone,
      email: r.email,
      web: r.web,
      category: toCategory(r.markertype),
    };
    // carry template-only extras (e.g. premium `corner` image) through untyped
    (s as unknown as { corner?: unknown }).corner = (
      r as unknown as { corner?: unknown }
    ).corner;
    return s;
  }

  // Every markertype "4" is a company location (big marker) — no dedupe. If the
  // feed has zero type-4 records, no company marker shows; map centering falls
  // back to the hardcoded Nashville constant (GMAP_USA_CENTER).
  function mapRaw(res: unknown): Store[] {
    const raw: RawStore[] = (res && (res as { data?: RawStore[] }).data) || [];
    return raw
      .map(toStore)
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  }

  async function loadAllStores(): Promise<Store[]> {
    if (allStores) return allStores;
    allStores = mapRaw(await ajax(endpoint));
    return allStores;
  }

  /**
   * Fetch stores for the current area. Phase 1: fetch the full set ONCE and let
   * the engine gate by radius client-side. Phase 2: swap to a server call scoped
   * by (center, radius) — the ONLY change is inside this function.
   */
  const fetchArea = debounce((center: LatLng, radius: number) => {
    if (!engine) return;
    $root.classList.add("is-loading");
    const done = (stores: Store[]) => {
      if (engine) engine.setStores(stores);
      $root.classList.remove("is-loading");
    };
    const fail = () => $root.classList.remove("is-loading");
    if (SERVER_SIDE_FILTERING) {
      const url = `${endpoint}?lat=${center.lat}&lng=${center.lng}&radius=${radius}`;
      ajax(url)
        .then((res) => done(mapRaw(res)))
        .catch(fail);
    } else {
      loadAllStores().then(done).catch(fail);
    }
  }, 350);

  // ---- list rendering ----
  function toResultData(store: Store) {
    return { ...store, distance: Math.round(store.distance || 0) };
  }

  function renderList(visible: Store[]) {
    $results.innerHTML = renderResults({ results: visible.map(toResultData) });
    bindResultClicks();
    // The list is re-rendered on every change; re-apply the highlight if the
    // selected store is still part of the visible set.
    if (selectedUuid) highlightSelected();
    // Re-apply a just-issued scroll that this re-render would otherwise have
    // thrown away (popup auto-pan → idle → refresh → re-render). Honoured only
    // briefly, so a later unrelated pan can't hijack the scroll position.
    if (pendingScroll) {
      if (
        pendingScroll.uuid === selectedUuid &&
        Date.now() - pendingScroll.at < 1500
      ) {
        scrollToSelected();
      } else {
        pendingScroll = null;
      }
    }
  }

  function bindResultClicks() {
    forEach($all("[data-place-uuid]", $results), ($el: HTMLElement) => {
      on($el, "click", (event: Event) => {
        // Let links inside the card (directions/phone/email/web) do their own
        // thing without ALSO opening the map popup.
        if ((event.target as HTMLElement).closest("a")) return;
        const uuid = $el.getAttribute("data-place-uuid");
        if (uuid) selectStore(uuid, true);
      });
    });
  }

  /**
   * Many CMS `corner` URLs 404 (the image row exists but the file was never
   * uploaded). Hide the wrapper on load failure so the card doesn't reserve an
   * empty gap / broken-image icon.
   *
   * `error` events don't bubble, so we listen in the CAPTURE phase on document —
   * one listener covers both the results list and the map popup (whose HTML is
   * injected by the engine, out of reach of renderList).
   */
  function hideBrokenCornerImages() {
    document.addEventListener(
      "error",
      (event) => {
        const $img = event.target as HTMLElement | null;
        if (!$img || $img.tagName !== "IMG") return;
        const $wrap = $img.parentElement;
        if (!$wrap) return;
        // className match avoids escaping the `:` in these namespaced classes
        const cls = $wrap.className || "";
        if (
          cls.indexOf("storeresult:corner") !== -1 ||
          cls.indexOf("storetip:corner") !== -1
        ) {
          $wrap.style.display = "none";
        }
      },
      true
    );
  }

  /** Mark the selected card, clear the others. */
  function highlightSelected() {
    forEach($all("[data-place-uuid]", $results), ($el: HTMLElement) => {
      $el.classList.toggle(
        "is-selected",
        $el.getAttribute("data-place-uuid") === selectedUuid
      );
    });
  }

  /** Bring the selected card to the top of the list. */
  function scrollResultToTop($el: HTMLElement) {
    // Decide by whether the panel is a SCROLL CONTAINER (desktop: overflow:auto),
    // NOT by whether it's currently overflowing. Otherwise a short result set
    // falls through to scrollIntoView, which scrolls the whole PAGE — the "page
    // scrolls by itself" glitch. Scrolling the panel is always page-safe (a no-op
    // when the item already fits). Mobile (panel not a scroll container) uses
    // scrollIntoView by design, since there the page IS the scroll surface.
    const panelScrolls =
      !!$resultsPanel &&
      /(auto|scroll)/.test(getComputedStyle($resultsPanel).overflowY);

    if (panelScrolls && $resultsPanel) {
      const delta =
        $el.getBoundingClientRect().top - $resultsPanel.getBoundingClientRect().top;
      $resultsPanel.scrollTo({
        top: $resultsPanel.scrollTop + delta,
        behavior: "auto",
      });
    } else {
      $el.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
  }

  /**
   * Single selection path shared by BOTH entry points (list click + map marker
   * click): scroll the card to the top of the list, highlight it, and open the
   * map popup. Marker clicks pass openPopup=false — the engine opens the popup
   * itself on `gmp-click`, so we'd otherwise open it twice.
   */
  function selectStore(uuid: string, openPopup: boolean) {
    selectedUuid = uuid;
    // Opening the popup can make Google auto-pan the map to fit it; in follow
    // mode that pan re-renders (and re-sorts) the list, destroying the node we
    // just scrolled to. Record the intent so renderList can re-apply it.
    pendingScroll = { uuid, at: Date.now() };
    highlightSelected();
    scrollToSelected();
    if (openPopup && engine) engine.openInfoWindow(uuid);
  }

  /** Scroll to the selected card, if it's currently in the DOM. */
  function scrollToSelected() {
    if (!selectedUuid) return;
    const $el = $("#locator-result-" + selectedUuid, $results) as
      | HTMLElement
      | undefined;
    if ($el) scrollResultToTop($el);
  }

  // ---- legend filter bar: single-select (like atlasconcorde.com/it) ----
  // "View all" (data-locator-filter="all") shows every category; clicking a
  // category shows ONLY that one. One item is active at a time.
  function wireLegend() {
    const $items = $all("[data-locator-filter]");
    const setActive = ($active: HTMLElement) => {
      forEach($items, ($el: HTMLElement) => {
        const isActive = $el === $active;
        $el.classList.toggle("is-active", isActive);
        $el.setAttribute("aria-pressed", String(isActive));
      });
    };
    forEach($items, ($btn: HTMLElement) => {
      on($btn, "click", () => {
        if (!engine) return;
        const key = $btn.getAttribute("data-locator-filter");
        let next: Record<Category, boolean>;
        if (key === "all") {
          next = { company: true, premium: true, dealer: true };
        } else if (CATEGORIES.indexOf(key as Category) !== -1) {
          next = { company: false, premium: false, dealer: false };
          next[key as Category] = true;
        } else {
          return;
        }
        activeFilters.company = next.company;
        activeFilters.premium = next.premium;
        activeFilters.dealer = next.dealer;
        engine.setCategoryFilters(next);
        setActive($btn);
      });
    });
  }

  // ---- distance select ----
  function wireDistance() {
    const $distanceWrap = $(".locator:search__distance", $root) as HTMLElement | undefined;
    const $distance = $("#locator-distance", $root) as HTMLSelectElement | undefined;
    if (!$distance) return;
    // v2 always has a center (geolocation or Nashville), so the radius selector is
    // usable immediately — drop the legacy "search an address first" gate.
    $distance.disabled = false;
    if ($distanceWrap) setDataAttr($distanceWrap, "is", "enabled");
    $distance.value = String(radiusMiles);
    on($distance, "change", () => {
      radiusMiles = Math.min(
        Number($distance.value) || GMAP_DEFAULT_RADIUS_MILES,
        GMAP_RADIUS_CAP_MILES
      );
      const state = engine ? engine.getState() : null;
      if (engine) engine.setCenter(state ? state.center : initialCenter, radiusMiles);
      navigateToMergedParams({ ...getUrlQueryParams(), radius: radiusMiles });
    });
  }

  // ---- searchaddress ----
  function wireSearch() {
    Searchaddress($root as HTMLElement, {
      onCoords: ({ lat, lng }) => {
        if (engine) engine.setCenter({ lat, lng }, radiusMiles);
      },
      onGeolocation: ({ lat, lng }) => {
        if (engine) engine.setCenter({ lat, lng }, radiusMiles);
      },
    }).init(undefined);
  }

  // ---- geolocation on load ----
  function geolocateOnLoad() {
    if (deep.center) return; // deeplink center wins
    if (!engine) return;
    if (!navigator.geolocation) {
      engine.setCenter(GMAP_USA_CENTER, radiusMiles); // Nashville fallback
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        engine && engine.setCenter({ lat: coords.latitude, lng: coords.longitude }, radiusMiles),
      () => engine && engine.setCenter(GMAP_USA_CENTER, radiusMiles) // deny → Nashville
    );
  }

  // ---- boot ----
  MapEngine(
    {
      container: $map,
      center: initialCenter,
      radiusMiles,
      minZoom: GMAP_MIN_ZOOM,
      mapId: GMAP_MAP_ID,
      restriction: GMAP_USA_BOUNDS,
      colorScheme: "FOLLOW_SYSTEM",
      panFollowsRadius,
      renderInfoWindowContent: (store) => renderInfoWindow(toResultData(store)),
    },
    {
      onReady: (inst) => {
        engine = inst;
        wireSearch();
        wireDistance();
        wireLegend();
        hideBrokenCornerImages();
        fetchArea(initialCenter, radiusMiles);
        geolocateOnLoad();
      },
      onStoresChanged: (visible) => renderList(visible),
      onBoundsSettled: (center, radius) => {
        if (SERVER_SIDE_FILTERING) fetchArea(center, radius);
      },
      onMarkerClick: (store) => {
        dataLayerStore("distributor location click", store.name);
        // Same behavior as a list click: scroll the card to the top + highlight.
        // The engine opens the popup itself, so don't open it again here.
        selectStore(store.uuid, false);
      },
    }
  )
    .init()
    .catch((err) => console.error("Storelocator v2 failed to init:", err));
}

/** Read-only legacy→new deeplink translation. Never re-emits old param names. */
function normalizeDeeplink(
  params: Record<string, string>
): { center?: LatLng; radius?: number } {
  const num = (v?: string) =>
    v != null && v !== "" && !isNaN(Number(v)) ? Number(v) : undefined;
  const lat = num(params.lat) ?? num(params.posLat);
  const lng = num(params.lng) ?? num(params.posLng);
  const radius = num(params.radius) ?? num(params.distance);
  const out: { center?: LatLng; radius?: number } = {};
  if (lat != null && lng != null) out.center = { lat, lng };
  if (radius != null) out.radius = radius;
  return out;
}
